/**
 * マスタデータの型定義
 *
 * ルール（core）とデータ（本パッケージ）を分離し、
 * バランス調整・史実の追加・新カードの実装がデータ差し替えで済むようにする。
 * docs/02-データ設計.md
 */

import type { AbilityRule, Arm, Effect, HoleCardRule } from '@tenka/core';

// ---------------------------------------------------------------------------
// 史実メモ — 学習機能の実体
// ---------------------------------------------------------------------------

/**
 * すべての国・武将・イベントに **必須**。バリデータで欠落を検出する。
 * docs/05-学習設計.md §4「史実の扱いに関する原則」を必ず読んでから書くこと。
 */
export interface HistoryNote {
  /** 1〜2文。カードやマスの上に出る短い要約 */
  readonly summary: string;

  /**
   * 学年別の本文。3段階すべて必須。
   *   1 = 小学校高学年（100字程度・平易な語彙）
   *   2 = 中学生（200字程度・教科書相当の用語）
   *   3 = 高校以上（400字程度・専門用語可、研究状況に触れる）
   */
  readonly detail: { readonly 1: string; readonly 2: string; readonly 3: string };

  /** 西暦。期間なら [開始, 終了] */
  readonly year?: number | readonly [number, number];

  /** 図鑑の分類・検索に使う。3〜6語 */
  readonly keywords: readonly string[];

  /**
   * 通説と異なる説がある場合に必ず書く。
   * 例:「桶狭間の奇襲説は近年の研究で正面攻撃説が有力とされる」
   */
  readonly caveat?: string;

  /**
   * ゲームの都合で史実を改変している場合に必ず書く。
   * 例:「盤上の石高は遊びやすさのための相対値です。実際の武蔵は約67万石」
   */
  readonly gameNote?: string;
}

// ---------------------------------------------------------------------------
// 盤面
// ---------------------------------------------------------------------------

export type SquareId = number; // 0..39

export type SquareKind =
  | 'province' // 国
  | 'capital' // 京
  | 'post' // 宿場
  | 'port' // 湊
  | 'barrier' // 関所
  | 'temple' // 寺社
  | 'battle' // 合戦
  | 'disaster' // 災害
  | 'council'; // 評定

export type Region =
  | 'kinai'
  | 'tokai'
  | 'kanto'
  | 'tohoku'
  | 'hokuriku'
  | 'sanin'
  | 'sanyo'
  | 'shikoku'
  | 'kyushu';

/**
 * 地形。**戦場札のうち2枚がこの兵種に固定される**（地の利）。
 * 1枚では効果がないことが実測で分かっている。docs/06 §4
 */
export type Terrain = 'mountain' | 'plain' | 'coast' | 'temple';

/** 地形 → 保証される兵種 */
export const TERRAIN_ARM: Readonly<Record<Terrain, Arm>> = {
  mountain: 'yumi', // 山国は弓
  plain: 'kiba', // 平野は騎馬
  coast: 'teppou', // 湊は鉄砲（第I章では足軽に読み替える）
  temple: 'souhei', // 寺社領は僧兵
};

export interface SquareBase {
  readonly id: SquareId;
  readonly kind: SquareKind;
  readonly name: string;
  readonly reading: string;
  readonly region: Region;
  readonly note: HistoryNote;
  readonly effects?: readonly Effect[];
}

export interface Province extends SquareBase {
  readonly kind: 'province';
  /** 盤上の石高ランク 1〜4。史実値ではなくバランス値 */
  readonly kokudaka: 1 | 2 | 3 | 4;
  /** 切り取りに必要な兵の基礎値 */
  readonly defense: number;
  readonly terrain: Terrain;
  /** 銀山・金山など。銭を産む */
  readonly yields?: readonly { readonly res: 'zeni' | 'koku'; readonly n: number }[];
  /** 太閤検地などの実値（万石）。学習表示に使う。gameNote と併せて必ず書く */
  readonly historicalKokudaka?: number;
}

export type Square = Province | SquareBase;

// ---------------------------------------------------------------------------
// 武将
// ---------------------------------------------------------------------------

/**
 * 武将の能力。
 *
 * **いつ発火するかを必ず書く。** 効果の配列だけを並べていた頃は、
 * 「毎ターン兵+1」と「開始時に兵+4」と「勝つと威信+2」が同じ形をしていて
 * 区別が付かず、engine は結局どれも適用できなかった（`@tenka/core` の
 * `warlord.ts` 冒頭）。
 */
export interface WarlordAbility {
  readonly name: string;
  readonly text: string;
  /** 発火する時点つきの効果 */
  readonly rules?: readonly AbilityRule[];
  /** 合戦での手勢札の増減。条件つき */
  readonly holeCardRules?: readonly HoleCardRule[];
  /** 時代より早く、自分の合戦の山札に加わる兵種 */
  readonly deckInject?: Arm;
  /** 築城の割引 */
  readonly castleDiscount?: number;
}

export interface Warlord {
  readonly id: string; // 'wl:nobunaga'
  readonly name: string;
  readonly reading: string;
  readonly clan: string;
  readonly color: string; // 家紋の伝統色
  readonly startSquare: SquareId;
  readonly ability: WarlordAbility;
  readonly note: HistoryNote;
}

// ---------------------------------------------------------------------------
// カード
// ---------------------------------------------------------------------------

export type CardType =
  | 'naisei' // 内政
  | 'gunji' // 軍事
  | 'bouryaku' // 謀略
  | 'gaikou' // 外交
  | 'kahou' // 家宝
  | 'kani'; // 官位

/**
 * 札の系統の呼び名。`Record<CardType, string>` なので、
 * 系統を1つ足すとここが埋まっていないことがコンパイル時に落ちる。
 * 画面側で同じ表を書き直すと id が人に見える（core の `CASTLE_LABEL` と同じ話）。
 */
export const CARD_TYPE_LABEL: Readonly<Record<CardType, string>> = {
  naisei: '内政',
  gunji: '軍事',
  bouryaku: '謀略',
  gaikou: '外交',
  kahou: '家宝',
  kani: '官位',
};

/** 札を切れる場面 */
export type CardTiming = 'council' | 'battle' | 'anytime' | 'reaction';

export const CARD_TIMING_LABEL: Readonly<Record<CardTiming, string>> = {
  council: '評定で切る',
  battle: '合戦で切る',
  anytime: 'いつでも切れる',
  reaction: '相手の手番に切る',
};

export interface Card {
  readonly id: string;
  readonly name: string;
  readonly reading: string;
  readonly type: CardType;
  readonly cost: { readonly zeni?: number; readonly gunryaku?: number };
  readonly timing: CardTiming;
  readonly effects: readonly Effect[];
  /** 家宝・官位の天下点 */
  readonly vp?: number;
  /** 解禁される章。鉄砲関連は 2 以降 */
  readonly availableFrom?: 1 | 2 | 3 | 4;
  readonly note?: HistoryNote;
}

// ---------------------------------------------------------------------------
// 史実イベント
// ---------------------------------------------------------------------------

export interface HistoricalEvent {
  readonly id: string;
  readonly year: number;
  readonly chapter: 1 | 2 | 3 | 4 | 5;
  /** era-law = 章の開始時に必ず発動する時代法 / incident = 評定マスで引く事件札 */
  readonly kind: 'era-law' | 'incident';
  readonly name: string;
  readonly reading: string;
  readonly effects: readonly Effect[];
  readonly note: HistoryNote;
}
